import { useEffect, useState } from 'react';
import {
  Buildings, Warning, CheckCircle, Circle, MagnifyingGlass,
} from '@phosphor-icons/react';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { useModelos } from '@/hooks/useLotesKanban';
import { useBuscarLotes } from '@/hooks/useBootstrap';
import type { Confianza, LoteCandidato, LoteBusqueda, BandejaLote } from '@/types';
import type { ProyectoCode } from '@/types/sitePlan';

// ============================================================
// Piezas compartidas por las DOS tarjetas de la bandeja: la de LOTE (varios
// codigos juntos, el camino normal) y la de CODIGO (ambiguos y sin sugerencia,
// donde el lote todavia no esta decidido). Viven acá y no en una de las dos
// para que no haya una copia que se quede atras.
// ============================================================

// Lote elegido para vincular — forma comun a candidato (motor), lote agrupado y
// resultado del buscador manual. Trae los DOS modelos (tramitado + reservado)
// para los chips de prefill del panel de vinculacion.
export type SelectedLote = {
  idLote: number;
  idBd?: string | null;
  proyecto?: string | null;
  idModeloTramitado?: number | null;
  modeloTramitado?: string | null;
  idModeloReservado?: number | null;
  modeloReservado?: string | null;
  tieneTramiteVigente: boolean;
};

export function selectedDeCandidato(c: LoteCandidato): SelectedLote {
  return {
    idLote: c.idLote, idBd: c.idBd, proyecto: c.proyecto,
    idModeloTramitado: c.idModeloTramitado, modeloTramitado: c.modeloTramitado,
    idModeloReservado: c.idModeloReservado, modeloReservado: c.modeloReservado,
    tieneTramiteVigente: c.tieneTramiteVigente,
  };
}

export function selectedDeBusqueda(l: LoteBusqueda): SelectedLote {
  return {
    idLote: l.idLote, idBd: l.idBd, proyecto: l.proyecto,
    idModeloTramitado: l.idModeloTramitado, modeloTramitado: l.modeloTramitado,
    idModeloReservado: l.idModeloReservado, modeloReservado: l.modeloReservado,
    tieneTramiteVigente: l.tieneTramiteVigente,
  };
}

export function selectedDeLote(l: BandejaLote): SelectedLote {
  return {
    idLote: l.idLote, idBd: l.idBd, proyecto: l.proyecto,
    idModeloTramitado: l.idModeloTramitado, modeloTramitado: l.modeloTramitado,
    idModeloReservado: l.idModeloReservado, modeloReservado: l.modeloReservado,
    tieneTramiteVigente: l.tieneTramiteVigente,
  };
}

// Chip de confianza con color semantico (tokens brand). Alta = verde (seguro),
// AltaPeroVerificar = amarillo (numerico que puede ser una fecha espuria),
// Media = gris (ambiguo, el humano elige entre 2-3).
const CONFIANZA_STYLE: Record<Confianza, { bg: string; text: string; label: string }> = {
  Alta:              { bg: 'bg-brand-green-200', text: 'text-white', label: 'Alta' },
  AltaPeroVerificar: { bg: 'bg-brand-yellow',    text: 'text-black', label: 'Verificar' },
  Media:             { bg: 'bg-brand-gray-400',  text: 'text-white', label: 'Ambiguo' },
};

export function ConfianzaChip({ confianza }: { confianza: Confianza }) {
  const s = CONFIANZA_STYLE[confianza];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

// Badge "ya tiene trámite" (señal de re-tramite, 9.6).
export function TramiteBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-brand-red-100 px-2 py-0.5 text-[10px] font-bold text-white"
      title="Este lote ya tiene un tramite vigente — vincularlo abre el camino re-tramite (9.6)"
    >
      <Warning size={11} weight="bold" /> Ya tiene trámite
    </span>
  );
}

// Los modelos que YA tiene el lote. SIEMPRE van rotulados: antes, cuando había
// uno solo (o cuando reservado y tramitado eran el mismo), se pintaba el nombre
// pelado y el operador no podía saber cuál de los dos estaba viendo. Son cosas
// distintas — «Reservado» sale del caso de venta vigente y «Tramitado» es
// Lotes.IDModDefecto — así que el nombre sin etiqueta no dice nada.
export function ModelosLine({ tramitado, reservado }: { tramitado?: string | null; reservado?: string | null }) {
  const base = 'flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-brand-gray-400';
  const Rot = ({ children }: { children: string }) =>
    <span className="font-medium text-brand-gray-500">{children}</span>;

  if (!reservado && !tramitado)
    return <span className="text-[11px] italic text-brand-gray-400">El lote no tiene modelo reservado ni tramitado</span>;

  // Mismo modelo en los dos campos: decirlo explícito. Repetir el nombre dos
  // veces haría pensar que son distintos y hay que compararlos.
  if (reservado && tramitado && reservado === tramitado)
    return <span className={base}><span><Rot>Reservado y tramitado:</Rot> {reservado}</span></span>;

  return (
    <span className={base}>
      {reservado && <span><Rot>Reservado:</Rot> {reservado}</span>}
      {tramitado && <span><Rot>Tramitado:</Rot> {tramitado}</span>}
    </span>
  );
}

// Chip de acceso rapido para prefilear el modelo (reservado / tramitado).
function ModeloChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
        active
          ? 'border-brand-green-200 bg-brand-green-100 text-black'
          : 'border-brand-gray-300 bg-white text-brand-gray-500 hover:bg-brand-gray-100'
      }`}
    >
      {label}
    </button>
  );
}

// Selector del modelo a vincular (opcional, 9.3): chips de acceso rapido
// (reservado = default, tramitado) + combo "u otro modelo". El combo es la
// fuente de verdad (id) de lo que se escribe en TramiteAD.IdModelo.
export function ModeloPicker({
  lote, idModelo, onChange,
}: { lote: SelectedLote; idModelo: number | null; onChange: (v: number | null) => void }) {
  const idRes = lote.idModeloReservado ?? null;
  const idTram = lote.idModeloTramitado ?? null;

  // Modelos activos del proyecto del lote (null -> query disabled).
  const proyecto = (lote.proyecto ?? null) as ProyectoCode | null;
  const { data: modelos = [] } = useModelos(proyecto);
  const baseOpts = modelos.map(m => ({ value: m.idModelo, label: m.nombre ?? `Modelo ${m.idModelo}` }));
  // Asegurar que el tramitado/reservado del lote esten como opcion del combo
  // aunque esten INACTIVOS (sino el combo se veria vacio al elegir ese chip).
  const extraOpts: { value: number; label: string }[] = [];
  if (idTram != null && !baseOpts.some(o => o.value === idTram))
    extraOpts.push({ value: idTram, label: `${lote.modeloTramitado ?? `Modelo ${idTram}`} (tramitado)` });
  if (idRes != null && idRes !== idTram && !baseOpts.some(o => o.value === idRes))
    extraOpts.push({ value: idRes, label: `${lote.modeloReservado ?? `Modelo ${idRes}`} (reservado)` });
  const modeloOptions = [...extraOpts, ...baseOpts];

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] font-medium text-brand-gray-500">Modelo del trámite</span>
        <span className="text-[10px] text-brand-gray-400">(opcional)</span>
        {idRes != null && (
          <ModeloChip
            active={idModelo === idRes}
            onClick={() => onChange(idRes)}
            label={`Reservado · ${lote.modeloReservado ?? idRes}`}
          />
        )}
        {idTram != null && idTram !== idRes && (
          <ModeloChip
            active={idModelo === idTram}
            onClick={() => onChange(idTram)}
            label={`Tramitado · ${lote.modeloTramitado ?? idTram}`}
          />
        )}
      </div>
      <SearchableSelect<number>
        options={modeloOptions}
        value={idModelo}
        onChange={onChange}
        placeholder="— Sin modelo —"
        emptyText="Sin modelos activos"
      />
      {/* El rotulo solo no alcanzaba: el operador leia «Modelo» y asumia que era
          el Modelo Tramitado del lote. Son cosas distintas y conviven. */}
      <p className="text-[10px] leading-tight text-brand-gray-400">
        Es el modelo <strong>con el que se tramitó este código</strong>: queda guardado en el trámite.
        Los dos chips de arriba son solo lectura y sirven de atajo.
      </p>
    </div>
  );
}

// Fila SELECCIONABLE (radio) — candidato del motor. Dos lineas: cabecera +
// modelos (ambos cuando difieren).
export function CandidatoRow({ c, selected, onSelect }: { c: LoteCandidato; selected: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full flex-col gap-1 rounded-lg border px-2.5 py-1.5 text-left text-sm transition-colors ${
        selected ? 'border-brand-green-200 bg-brand-green-100/10' : 'border-brand-gray-200 bg-white hover:bg-brand-gray-100'
      }`}
    >
      <div className="flex w-full items-center gap-2">
        {selected
          ? <CheckCircle size={16} weight="fill" className="shrink-0 text-brand-green-200" />
          : <Circle size={16} weight="bold" className="shrink-0 text-brand-gray-300" />}
        <Buildings size={15} weight="bold" className="shrink-0 text-brand-gray-400" />
        <span className="font-mono font-semibold text-brand-gray-500">{c.idBd ?? `Lote ${c.idLote}`}</span>
        {c.proyecto && <span className="rounded-full bg-brand-gray-100 px-2 py-0.5 text-[11px] text-brand-gray-400">{c.proyecto}</span>}
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {c.tieneTramiteVigente && <TramiteBadge />}
          <ConfianzaChip confianza={c.confianza} />
        </span>
      </div>
      {(c.modeloTramitado || c.modeloReservado) && (
        <div className="pl-6">
          <ModelosLine tramitado={c.modeloTramitado} reservado={c.modeloReservado} />
        </div>
      )}
    </button>
  );
}

// Buscador manual inline (escape de 9.4). Debounce local; el query se activa a
// los 2+ caracteres. Picking un resultado lo entrega al padre (= seleccionarlo).
export function BuscarLoteInline({ onPick, selectedIdLote }: { onPick: (l: LoteBusqueda) => void; selectedIdLote: number | null }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data: resultados = [], isFetching } = useBuscarLotes(debounced);
  const term = debounced.trim();

  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-brand-gray-200 bg-brand-bg p-2.5">
      <div className="flex items-center gap-2 rounded border border-brand-gray-200 bg-white px-2 py-1.5">
        <MagnifyingGlass size={14} weight="bold" className="shrink-0 text-brand-gray-400" />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Buscar lote por IDBD o identificador (ej. VN-K.19)…"
          className="w-full bg-transparent text-sm outline-none placeholder:text-brand-gray-300"
        />
      </div>

      {term.length > 0 && term.length < 2 && (
        <p className="text-[10px] text-brand-gray-400">Escribí al menos 2 caracteres.</p>
      )}
      {term.length >= 2 && (
        <div className="flex flex-col gap-1">
          {isFetching && <p className="text-[10px] text-brand-gray-400">Buscando…</p>}
          {!isFetching && resultados.length === 0 && (
            <p className="text-[10px] italic text-brand-gray-400">Sin resultados.</p>
          )}
          {resultados.map(l => {
            const sel = selectedIdLote === l.idLote;
            return (
              <button
                key={l.idLote}
                type="button"
                onClick={() => onPick(l)}
                className={`flex flex-col gap-1 rounded-lg border px-2.5 py-1.5 text-left text-sm transition-colors ${
                  sel ? 'border-brand-green-200 bg-brand-green-100/10' : 'border-brand-gray-200 bg-white hover:bg-brand-gray-100'
                }`}
              >
                <div className="flex w-full items-center gap-2">
                  {sel
                    ? <CheckCircle size={15} weight="fill" className="shrink-0 text-brand-green-200" />
                    : <Circle size={15} weight="bold" className="shrink-0 text-brand-gray-300" />}
                  <span className="font-mono font-semibold text-brand-gray-500">{l.idBd ?? `Lote ${l.idLote}`}</span>
                  {l.proyecto && <span className="rounded-full bg-brand-gray-100 px-2 py-0.5 text-[11px] text-brand-gray-400">{l.proyecto}</span>}
                  {l.tieneTramiteVigente && <span className="ml-auto"><TramiteBadge /></span>}
                </div>
                {(l.modeloTramitado || l.modeloReservado) && (
                  <div className="pl-6">
                    <ModelosLine tramitado={l.modeloTramitado} reservado={l.modeloReservado} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
